import type { User } from "@/types"

const STORAGE_KEY = "fyp:currentUser"
const TTL_MS = 10 * 60 * 1000 // 10 minutes

interface CachedSnapshot {
  user: User
  savedAt: number
}

/**
 * Persists the current user to sessionStorage so subsequent page
 * refreshes within the TTL window can skip the /users/me call.
 */
export const saveUserSnapshot = (user: User): void => {
  try {
    const payload: CachedSnapshot = { user, savedAt: Date.now() }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Storage full or unavailable — silently ignore.
  }
}

/**
 * Loads a previously cached user snapshot if it is still within the
 * 10-minute TTL window.
 *
 * @returns The cached User or null when expired / missing / corrupt.
 */
export const loadUserSnapshot = (): User | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const snapshot: CachedSnapshot = JSON.parse(raw)
    if (Date.now() - snapshot.savedAt > TTL_MS) {
      clearUserSnapshot()
      return null
    }

    return snapshot.user
  } catch {
    clearUserSnapshot()
    return null
  }
}

/**
 * Removes the cached user snapshot from sessionStorage.
 */
export const clearUserSnapshot = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore errors from unavailable storage.
  }
}

// ---------------------------------------------------------------------------
// Global 401 invalidation bridge
// ---------------------------------------------------------------------------

type AuthInvalidationListener = () => void
let invalidationListener: AuthInvalidationListener | null = null

/**
 * Registers a callback invoked whenever any backend request returns 401.
 * AuthProvider uses this to clear React state without navigating to /login.
 *
 * @returns A cleanup function that unregisters the listener.
 */
export const onGlobal401 = (listener: AuthInvalidationListener): (() => void) => {
  invalidationListener = listener
  return () => {
    invalidationListener = null
  }
}

/**
 * Called by the SDK error interceptor when a 401 is received.
 * Clears the cached snapshot and notifies the React auth layer.
 */
export const notifyGlobal401 = (): void => {
  clearUserSnapshot()
  invalidationListener?.()
}
